import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于西瓜视频',
    description: '西瓜视频是一个领先的在线高清视频平台，提供蜜桃视频、红桃视频、樱桃视频等丰富内容分类。了解我们的品牌故事、核心优势和联系方式。',
    url: 'https://www.xigua-video.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '西瓜视频',
      url: 'https://www.xigua-video.com/',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '西瓜视频需要下载安装吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要！西瓜视频完全基于网页技术，您只需在手机或电脑浏览器中打开我们的网站即可直接观看视频，无需下载任何客户端软件或浏览器插件。',
        },
      },
      {
        '@type': 'Question',
        name: '西瓜视频的视频内容是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，西瓜视频上的所有视频内容均免费提供观看。我们致力于为所有用户提供高质量的免费在线影视体验，让每个人都能轻松享受追剧乐趣。',
        },
      },
      {
        '@type': 'Question',
        name: '观看视频会卡顿缓冲吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '西瓜视频采用先进的流媒体传输技术和智能CDN调度系统，能够根据您的网络状况自动调整画质，确保流畅的播放体验。在正常网络环境下，可以实现不缓冲的高清播放。',
        },
      },
      {
        '@type': 'Question',
        name: '支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '西瓜视频支持所有主流设备，包括智能手机（iOS和Android）、平板电脑、笔记本电脑和台式电脑。只要是支持现代浏览器的设备，都可以流畅访问我们的网站。',
        },
      },
      {
        '@type': 'Question',
        name: '蜜桃视频、红桃视频、樱桃视频是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '蜜桃视频、红桃视频、樱桃视频是西瓜视频平台上的特色内容分类频道，分别提供不同类型的影视内容资源。您可以通过首页的分类导航或搜索功能快速找到对应频道的精彩视频。',
        },
      },
    ],
  };

  const stats = [
    { value: '5000万+', label: '月活跃用户', icon: 'ri-user-star-line' },
    { value: '10万+', label: '影视资源', icon: 'ri-film-line' },
    { value: '99.9%', label: '播放流畅率', icon: 'ri-flashlight-line' },
    { value: '7×24', label: '全天候服务', icon: 'ri-24-hours-line' },
  ];

  const teamValues = [
    {
      icon: 'ri-heart-pulse-line',
      title: '用户至上',
      desc: '始终将用户体验放在首位，不断优化产品功能和服务质量，让每一位用户都能享受到极致的观影体验。',
    },
    {
      icon: 'ri-lightbulb-flash-line',
      title: '技术创新',
      desc: '持续投入技术研发，在流媒体传输、智能推荐、画质优化等领域保持行业领先地位。',
    },
    {
      icon: 'ri-global-line',
      title: '内容丰富',
      desc: '不断扩充影视资源库，与国内外优质内容方合作，为用户提供更多元化的内容选择。',
    },
    {
      icon: 'ri-shield-star-line',
      title: '安全第一',
      desc: '严格遵守内容安全和用户隐私保护规范，为用户营造绿色、安全、健康的网络观影环境。',
    },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <main role="main">
        <Breadcrumb items={breadcrumbItems} />

        <section className="px-4 md:px-6 py-16 md:py-20 bg-background-50" aria-labelledby="about-heading">
          <div className="max-w-4xl mx-auto text-center">
            <h1 id="about-heading" className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-6">
              关于西瓜视频
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-3xl mx-auto">
              西瓜视频是一个领先的在线高清视频平台，
              致力于为用户提供<span className="text-primary-600 font-medium">蜜桃视频</span>、
              <span className="text-primary-600 font-medium">红桃视频</span>、
              <span className="text-primary-600 font-medium">樱桃视频</span>等丰富多样的影视内容。
              我们相信，优质的视频内容应该触手可及，无需复杂的下载安装流程，
              只需打开网页即可畅享高清流畅的观影体验。
            </p>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="text-center p-6 rounded-2xl bg-background-50 border border-background-200/70"
                >
                  <div className={`w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-3 ${
                    index % 2 === 0 ? 'bg-primary-100 text-primary-600' : 'bg-accent-100 text-accent-700'
                  }`}>
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-900 mb-1 font-heading">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16 bg-background-50" aria-labelledby="story-heading">
          <div className="max-w-4xl mx-auto">
            <h2 id="story-heading" className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-8">
              我们的故事
            </h2>
            <div className="space-y-6 text-foreground-700 leading-relaxed">
              <p className="text-base">
                西瓜视频诞生于一个简单的想法：<strong className="text-foreground-900">让每个人都能轻松享受高清视频</strong>。
                在这个移动互联网高速发展的时代，我们看到了用户对于便捷、高质量视频内容日益增长的需求。
              </p>
              <p className="text-base">
                传统的视频观看方式往往需要下载专用播放器、安装各种插件，甚至还需要注册复杂的账号。
                西瓜视频打破了这些障碍——我们将最先进的流媒体技术融入网页端，
                让用户在任何设备上打开浏览器就能立即享受高清视频。
              </p>
              <p className="text-base">
                从最初的几十部影片起步，到今天拥有超过10万+的影视资源库，
                西瓜视频始终坚持"用户至上"的理念。我们不断优化产品体验，从蜜桃视频、红桃视频到樱桃视频等多个内容频道，
                为用户提供更丰富、更精准的内容选择。
              </p>
              <p className="text-base">
                展望未来，西瓜视频将继续深耕在线视频领域，持续技术创新和内容拓展，
                让流畅不缓冲的高清播放体验惠及更多用户。无论您身处何地、使用何种设备，
                西瓜视频都将是您最值得信赖的在线观影伙伴。
              </p>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100" aria-labelledby="values-heading">
          <div className="max-w-7xl mx-auto">
            <h2 id="values-heading" className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              我们的价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {teamValues.map((value, index) => (
                <div
                  key={index}
                  className="p-6 rounded-2xl bg-background-50 border border-background-200/70 text-center"
                >
                  <div className={`w-14 h-14 mx-auto rounded-xl flex items-center justify-center mb-4 ${
                    index % 2 === 0 ? 'bg-primary-100 text-primary-600' : 'bg-accent-100 text-accent-700'
                  }`}>
                    <i className={`${value.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-semibold text-foreground-900 mb-2 text-base">
                    {value.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {value.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16 bg-background-50" aria-labelledby="contact-heading">
          <div className="max-w-4xl mx-auto">
            <h2 id="contact-heading" className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-8">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-12 h-12 mx-auto rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center mb-3">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-sm text-foreground-600">contact@xigua-video.com</p>
              </div>
              <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-12 h-12 mx-auto rounded-xl bg-accent-100 text-accent-700 flex items-center justify-center mb-3">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <h3 className="font-semibold text-foreground-900 mb-1">客服热线</h3>
                <p className="text-sm text-foreground-600">400-888-8888</p>
              </div>
              <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-12 h-12 mx-auto rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center mb-3">
                  <i className="ri-map-pin-line text-xl"></i>
                </div>
                <h3 className="font-semibold text-foreground-900 mb-1">公司地址</h3>
                <p className="text-sm text-foreground-600">北京市朝阳区科技园区西瓜大厦</p>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100" aria-labelledby="faq-heading">
          <div className="max-w-4xl mx-auto">
            <h2 id="faq-heading" className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              常见问题
            </h2>
            <div className="space-y-4">
              {faqSchema.mainEntity.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <h3 className="text-base font-medium text-foreground-900 pr-4">
                      {faq.name}
                    </h3>
                    <div className="w-6 h-6 rounded-full bg-background-100 flex items-center justify-center flex-shrink-0 group-open:rotate-45 transition-transform duration-200">
                      <i className="ri-add-line text-foreground-600 text-sm"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.acceptedAnswer.text}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              开始你的观影之旅
            </h2>
            <p className="text-foreground-600 mb-6">
              无需下载，无需注册，打开网页即可畅享高清视频
            </p>
            <Link
              to="/category/recommended"
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full transition-all duration-300 hover:scale-105 text-sm md:text-base whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-line"></i>
              立即观看
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}